import * as React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/core/v2/toaster';

import { getResidentMe, updateResidentCustomFieldValues } from '@/api/residents';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';
import { useUser } from '@/hooks/v2/use-user';

export function SettingsAdditionalDetails(): React.JSX.Element | null {
  const { user, community } = useUser();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['resident', 'me', community?.id],
    queryFn: () => getResidentMe(community!.id),
    enabled: !!community?.id && !!user?.id,
  });

  const customFields = data?.custom_fields ?? [];
  const customFieldValues = data?.custom_field_values ?? {};

  const [values, setValues] = React.useState<Record<string, string>>(() => ({ ...customFieldValues }));

  React.useEffect(() => {
    setValues((prev) => ({ ...customFieldValues, ...prev }));
  }, [customFieldValues]);

  const mutation = useMutation({
    mutationFn: (payload: Record<string, string | null>) =>
      updateResidentCustomFieldValues(community!.id, user!.id, payload),
    onSuccess: (res, variables) => {
      setValues((prev) => ({ ...prev, ...res.data }));
      queryClient.invalidateQueries({ queryKey: ['resident', 'me', community?.id] });
      const names = customFields.filter((f) => f.id in (variables ?? {})).map((f) => f.name);
      const suffix = names.length > 0 ? `: ${names.join(', ')}` : '';
      toast.success(`Saved${suffix}`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to save');
    },
  });

  if (!community?.id || !user?.id) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title="Additional details"
        subheader="Extra information for your profile in this community."
        subheaderTypographyProps={{ color: 'text.secondary' }}
      />
      <CardContent sx={{ pt: 0 }}>
        {isLoading ? (
          <Stack spacing={2}>
            <Skeleton height={40} variant="rectangular" />
            <Skeleton height={40} variant="rectangular" />
          </Stack>
        ) : isError ? (
          <Typography variant="body2" color="text.secondary">
            Unable to load additional details.
          </Typography>
        ) : customFields.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No additional details for this community.
          </Typography>
        ) : (
          <PropertyList divider={<Divider />} orientation="vertical" sx={{ '--PropertyItem-padding': '12px 24px' }}>
            {customFields.map((field) => {
              const currentValue = values[field.id] ?? '';
              const savedValue = customFieldValues[field.id] ?? '';
              const hasChanged = field.editable_by_member && currentValue !== savedValue;
              const isSavingThis = mutation.isPending && mutation.variables != null && field.id in mutation.variables;

              return (
                <PropertyItem
                  key={field.id}
                  name={
                    <Typography component="span" variant="subtitle2" fontWeight={600} color="text.primary">
                      {field.name}
                    </Typography>
                  }
                  value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <TextField
                        size="small"
                        disabled={!field.editable_by_member}
                        placeholder={field.editable_by_member ? `Enter ${field.name}` : undefined}
                        value={currentValue}
                        onChange={(e) =>
                          setValues((prev) => ({ ...prev, [field.id]: e.target.value }))
                        }
                        sx={{ minWidth: 200, maxWidth: 360, flex: 1 }}
                      />
                      {hasChanged && (
                        <LoadingButton
                          size="small"
                          variant="contained"
                          loading={isSavingThis}
                          onClick={() => mutation.mutate({ [field.id]: currentValue || null })}
                        >
                          Save
                        </LoadingButton>
                      )}
                    </Box>
                  }
                />
              );
            })}
          </PropertyList>
        )}
      </CardContent>
    </Card>
  );
}
